/* eslint-disable import/order */
import { useCallback, useRef, useState } from 'react'
import epsClient from 'enrollment-platform/api/eps.client'
import { message } from 'antd'

import type { EmployeeHandbookLanguage } from './useEmployeeHandbookUpload'

type UploadState =
  | 'idle'
  | 'presigning'
  | 'uploading'
  | 'saving'
  | 'success'
  | 'error'

type PresignedUploadUrlDto = {
  presignedUploadUrl: string
  s3Key: string
}

export type CompleteHealthplanChecklistUploadDto = {
  fileName: string
  s3Key: string
  language: EmployeeHandbookLanguage
  year: number
}

type Options = {
  clientId?: string
  clientPublicKey?: string
  language?: EmployeeHandbookLanguage
  year?: number
  onOk?: (success?: boolean) => void
  onSuccess?: () => void
  onSaved?: (payload: CompleteHealthplanChecklistUploadDto) => void
}

export function useHealthplanChecklistUpload(opts: Options) {
  const clientKey = opts.clientPublicKey ?? opts.clientId ?? ''
  const successCb = opts.onSuccess ?? opts.onOk
  const defaultLanguage: EmployeeHandbookLanguage = opts.language ?? 'english'
  const year = opts.year ?? new Date().getFullYear()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [state, setState] = useState<UploadState>('idle')

  const handleUpload = useCallback(
    async (
      selectedFile: File | null | undefined,
      languageOverride?: EmployeeHandbookLanguage
    ) => {
      if (!selectedFile || !clientKey) {
        message.error('Please select a file and a client before uploading.')
        return
      }

      const languageToUse = languageOverride ?? defaultLanguage

      setIsUploading(true)
      setState('presigning')

      try {
        const { name: fileName, type } = selectedFile

        const presignRes = await epsClient.post<PresignedUploadUrlDto>(
          `/api/v1/employee-experience/customers/${clientKey}/healthplan-checklist/presigned-upload-url`,
          {
            fileName,
            language: languageToUse,
            year,
          }
        )

        const { presignedUploadUrl, s3Key } = presignRes.data
        if (!presignedUploadUrl || !s3Key) {
          message.error('An error occurred. Please try again.')
          setState('error')
          return
        }

        setState('uploading')
        const putRes = await fetch(presignedUploadUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: { 'Content-Type': type || 'application/pdf' },
        })
        if (!putRes.ok) {
          const errorText = await putRes.text().catch(() => '')
          console.error('presigned upload failed', putRes.status, errorText)
          message.error('An error occurred. Please try again.')
          setState('error')
          return
        }

        setState('saving')
        const payload: CompleteHealthplanChecklistUploadDto = {
          fileName,
          s3Key,
          language: languageToUse,
          year,
        }
        await epsClient.post(
          `/api/v1/employee-experience/customers/${clientKey}/healthplan-checklist/complete`,
          payload
        )
        opts.onSaved?.(payload)

        setState('success')
        message.success('Healthplan checklist uploaded and saved.')
        successCb?.(true)
      } catch (error: any) {
        console.error(error)
        message.error(
          `Failed to upload/save. Error: ${error?.message ?? error}`
        )
        setState('error')
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [clientKey, defaultLanguage, year, successCb, opts.onSaved]
  )

  return {
    fileInputRef,
    handleUpload,
    isUploading,
    state,
  }
}
